import {
  Box,
  Paper,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { useCallback, useState, useEffect } from 'react';
import './mitre-attack.scss';
import {
  ConvergeCheckbox,
  ConvergeDetailAccordion,
  NewDateField,
} from '../../../presentation';
import { IClaimDetailsProps } from './claim-details';
import { useOutletContext } from 'react-router-dom';
import {
  useCloseClaimDetailsQuery,
  useUpsertClaimInformationMutation,
  useClaimInformationQuery,
} from '@converge/graphql';
import { useConvergeSnackBar } from '../../../contexts';
import dayjs from 'dayjs';
import { validateSetErrors } from '../utils';

type MitreRadioValue = 'notApplicable' | 'dataAvailable' | null;

const MitreAttack = () => {
  const { notify } = useConvergeSnackBar();
  const { claimUuid } = useOutletContext<IClaimDetailsProps>();
  const [mitreRadioValue, setMitreRadioValue] = useState<MitreRadioValue>(null);
  const [isMitraAttact, setIsMitraAttact] = useState<boolean>(false);
  const [isMitrasentAttact, setIsMitraSentAttact] = useState<boolean>(false);
  const [dateFieldErrors, setDateFieldErrors] = useState<{
    mitreAttackReceivedDate: boolean;
    mitreAttackSentDate: boolean;
    mitreAttackReceivedDateMessage?: string;
    mitreAttackSentDateMessage?: string;
  }>({
    mitreAttackReceivedDate: false,
    mitreAttackSentDate: false,
  });

  const { refetch: refetchCloseClaimDetails } = useCloseClaimDetailsQuery({
    claimUuid: claimUuid || '',
  });

  const {
    data: claimInfoData,
    loading: claimInfoLoading,
    error: claimInfoError,
  } = useClaimInformationQuery(claimUuid || '');

  const { upsertClaimInformation } = useUpsertClaimInformationMutation();

  const formMethods = useForm({
    defaultValues: {
      mitreAttackReceivedDate: '',
      mitreAttackSentDate: '',
    },
  });

  useEffect(() => {
    if (claimInfoData) {
      const {
        mitreAttackNotApplicable,
        mitreAttackReceivedDate,
        mitreAttackSentDate,
      } = claimInfoData.claims.edges[0].node;

      if (mitreAttackNotApplicable) {
        setMitreRadioValue('notApplicable');
        return;
      }

      if (mitreAttackReceivedDate || mitreAttackSentDate) {
        setMitreRadioValue('dataAvailable');
      }

      if (mitreAttackReceivedDate) {
        const formattedDate = dayjs(mitreAttackReceivedDate).format(
          'YYYY-MM-DD'
        );
        formMethods.setValue('mitreAttackReceivedDate', formattedDate);
        setIsMitraAttact(true);
      }

      if (mitreAttackSentDate) {
        const formattedDate = dayjs(mitreAttackSentDate).format('YYYY-MM-DD');
        formMethods.setValue('mitreAttackSentDate', formattedDate);
        setIsMitraSentAttact(true);
      }
    }
  }, [claimInfoData, formMethods]);

  const today = new Date();
  const [expandedAccordions, setExpandedAccordions] = useState<
    Record<string, boolean>
  >({
    mitreAttack: true,
  });

  const handleAccordionChange =
    (accordionKey: string) =>
    (_event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpandedAccordions((prev: Record<string, boolean>) => ({
        ...prev,
        [accordionKey]: newExpanded,
      }));
    };

  type FieldNames = 'mitreAttackReceivedDate' | 'mitreAttackSentDate';

  const MitreAttactHandler = useCallback(
    async (date: string | null, fieldName: FieldNames) => {
      try {
        await upsertClaimInformation({
          claimInput: {
            claimUuid,
            [fieldName]: date,
            mitreAttackNotApplicable: false,
          },
        });

        await refetchCloseClaimDetails();

        const fieldLabel =
          fieldName === 'mitreAttackReceivedDate'
            ? 'data received'
            : 'data sent to counsel';
        notify(`MITRE ATT&CK ${fieldLabel} has been updated`, {
          variant: 'success',
        });
      } catch (error) {
        const fieldLabel =
          fieldName === 'mitreAttackReceivedDate'
            ? 'data received'
            : 'data sent';
        notify(`Failed to update ${fieldLabel} date`, { variant: 'error' });
      }
    },
    [claimUuid, notify, refetchCloseClaimDetails, upsertClaimInformation]
  );

  const handleRadioChange = useCallback(
    async (value: MitreRadioValue) => {
      setMitreRadioValue(value);

      if (value === 'notApplicable') {
        // Reset checkboxes and date fields locally
        setIsMitraAttact(false);
        setIsMitraSentAttact(false);
        formMethods.setValue('mitreAttackReceivedDate', '', {
          shouldValidate: false,
        });
        formMethods.resetField('mitreAttackReceivedDate', { defaultValue: '' });
        formMethods.setValue('mitreAttackSentDate', '', {
          shouldValidate: false,
        });
        formMethods.resetField('mitreAttackSentDate', { defaultValue: '' });
        setDateFieldErrors({
          mitreAttackReceivedDate: false,
          mitreAttackSentDate: false,
        });

        try {
          await upsertClaimInformation({
            claimInput: {
              claimUuid,
              mitreAttackNotApplicable: true,
              mitreAttackReceivedDate: null,
              mitreAttackSentDate: null,
            },
          });
          await refetchCloseClaimDetails();
          notify('MITRE ATT&CK marked as not applicable', {
            variant: 'success',
          });
        } catch {
          notify('Failed to update MITRE ATT&CK status', { variant: 'error' });
        }
      } else if (value === 'dataAvailable') {
        // No API call here — wait for a checkbox to be selected
      } else {
        // null — neither radio selected, clear everything
        setIsMitraAttact(false);
        setIsMitraSentAttact(false);
        formMethods.setValue('mitreAttackReceivedDate', '', {
          shouldValidate: false,
        });
        formMethods.resetField('mitreAttackReceivedDate', { defaultValue: '' });
        formMethods.setValue('mitreAttackSentDate', '', {
          shouldValidate: false,
        });
        formMethods.resetField('mitreAttackSentDate', { defaultValue: '' });
        setDateFieldErrors({
          mitreAttackReceivedDate: false,
          mitreAttackSentDate: false,
        });

        try {
          await upsertClaimInformation({
            claimInput: {
              claimUuid,
              mitreAttackNotApplicable: false,
              mitreAttackReceivedDate: null,
              mitreAttackSentDate: null,
            },
          });
          await refetchCloseClaimDetails();
        } catch {
          notify('Failed to update MITRE ATT&CK status', { variant: 'error' });
        }
      }
    },
    [
      claimUuid,
      formMethods,
      notify,
      refetchCloseClaimDetails,
      upsertClaimInformation,
    ]
  );

  if (claimInfoLoading) {
    return (
      <Box className="claim-mitre-attack">
        <Paper elevation={0}>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            Loading...
          </Box>
        </Paper>
      </Box>
    );
  }

  if (claimInfoError) {
    return (
      <Box className="claim-mitre-attack">
        <Paper elevation={0}>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            Error loading MITRE ATT&CK data
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box className="claim-mitre-attack">
      <Paper elevation={0}>
        <FormProvider {...formMethods}>
          <ConvergeDetailAccordion
            expanded={expandedAccordions['mitreAttack']}
            onChange={handleAccordionChange('mitreAttack')}
            title="MITRE ATT&CK"
            description="Additional data will be available and accessible here in the future."
            detailsClassName="accordion-details-style"
            paddingBottom="40px"
            testId="mitre-attack-accordion"
          >
            <Box className="mitre-radio-group">
              <RadioGroup
                value={mitreRadioValue ?? ''}
                onChange={(_e, val) =>
                  handleRadioChange(val as MitreRadioValue)
                }
              >
                <FormControlLabel
                  value="notApplicable"
                  control={
                    <Radio
                      className={`radio-button ${
                        mitreRadioValue === 'notApplicable'
                          ? 'checked'
                          : 'unchecked'
                      }`}
                      data-testid="not-applicable-radio"
                      onClick={() => {
                        if (mitreRadioValue === 'notApplicable')
                          handleRadioChange(null);
                      }}
                    />
                  }
                  label={
                    <Typography className="mitre-radio">
                      Not applicable
                    </Typography>
                  }
                />
                <FormControlLabel
                  value="dataAvailable"
                  control={
                    <Radio
                      className={`radio-button ${
                        mitreRadioValue === 'dataAvailable'
                          ? 'checked'
                          : 'unchecked'
                      }`}
                      data-testid="data-available-radio"
                      onClick={() => {
                        if (mitreRadioValue === 'dataAvailable')
                          handleRadioChange(null);
                      }}
                    />
                  }
                  label={
                    <Typography className="mitre-radio">
                      Data available
                    </Typography>
                  }
                />
              </RadioGroup>
            </Box>

            {mitreRadioValue === 'dataAvailable' && (
              <Box className="mitre-data-available-section">
                {/* MITRE ATT&CK data sent to counsel */}
                <Box className="mitre-checkbox-group">
                  <Box className="mitre-checkbox-row">
                    <ConvergeCheckbox
                      name="claim"
                      onChange={(checked) => {
                        setIsMitraSentAttact(checked);
                        if (checked) {
                          const todayDayjs = dayjs();
                          const todayFormatted =
                            todayDayjs.format('YYYY-MM-DD');
                          const todayISO = todayDayjs
                            .startOf('day')
                            .toISOString();
                          formMethods.setValue(
                            'mitreAttackSentDate',
                            todayFormatted,
                            { shouldValidate: false }
                          );
                          MitreAttactHandler(todayISO, 'mitreAttackSentDate');
                        } else {
                          formMethods.setValue('mitreAttackSentDate', '', {
                            shouldValidate: false,
                          });
                          formMethods.resetField('mitreAttackSentDate', {
                            defaultValue: '',
                          });
                          setDateFieldErrors((prev) => ({
                            ...prev,
                            mitreAttackSentDate: false,
                          }));
                          MitreAttactHandler(null, 'mitreAttackSentDate');
                        }
                      }}
                      checked={isMitrasentAttact}
                      dataTestId="data-sent-to-counsel-checkbox"
                    />
                    <Typography className="mitre-checkbox">
                      MITRE ATT&CK data sent to counsel
                    </Typography>
                  </Box>
                  <Box className="mitre-date-field">
                    <NewDateField
                      key={isMitrasentAttact ? 'enabled' : 'disabled'}
                      disabled={!isMitrasentAttact}
                      name="mitreAttackSentDate"
                      maxDate={today}
                      disableValidation={true}
                      helperText={
                        dateFieldErrors.mitreAttackSentDate
                          ? dateFieldErrors.mitreAttackSentDateMessage ||
                            'Invalid date format'
                          : ''
                      }
                      error={dateFieldErrors.mitreAttackSentDate}
                      label="DATE SENT"
                      onDateChange={(date, inputValue) => {
                        if (isMitrasentAttact) {
                          const { isInvalid, errorMessage } = validateSetErrors(
                            'mitreAttackSentDate',
                            date,
                            inputValue,
                            formMethods
                          );
                          setDateFieldErrors((prev) => ({
                            ...prev,
                            mitreAttackSentDate: isInvalid,
                            mitreAttackSentDateMessage: errorMessage,
                          }));
                          if (!isInvalid && date) {
                            MitreAttactHandler(
                              date ? date.toISOString() : null,
                              'mitreAttackSentDate'
                            );
                          }
                        }
                      }}
                      data-testid="mitre-attack-sent-date-input"
                    />
                  </Box>
                </Box>

                {/* MITRE ATT&CK data received */}
                <Box className="mitre-checkbox-group">
                  <Box className="mitre-checkbox-row">
                    <ConvergeCheckbox
                      name="claim"
                      onChange={(checked) => {
                        setIsMitraAttact(checked);
                        if (checked) {
                          const todayDayjs = dayjs();
                          const todayFormatted =
                            todayDayjs.format('YYYY-MM-DD');
                          const todayISO = todayDayjs
                            .startOf('day')
                            .toISOString();
                          formMethods.setValue(
                            'mitreAttackReceivedDate',
                            todayFormatted,
                            { shouldValidate: false }
                          );
                          MitreAttactHandler(
                            todayISO,
                            'mitreAttackReceivedDate'
                          );
                        } else {
                          formMethods.setValue('mitreAttackReceivedDate', '', {
                            shouldValidate: false,
                          });
                          formMethods.resetField('mitreAttackReceivedDate', {
                            defaultValue: '',
                          });
                          setDateFieldErrors((prev) => ({
                            ...prev,
                            mitreAttackReceivedDate: false,
                          }));
                          MitreAttactHandler(null, 'mitreAttackReceivedDate');
                        }
                      }}
                      checked={isMitraAttact}
                      dataTestId="data-received-checkbox"
                    />
                    <Typography className="mitre-checkbox">
                      MITRE ATT&CK data received
                    </Typography>
                  </Box>
                  <Box className="mitre-date-field">
                    <NewDateField
                      key={isMitraAttact ? 'enabled' : 'disabled'}
                      disabled={!isMitraAttact}
                      name="mitreAttackReceivedDate"
                      maxDate={today}
                      disableValidation={true}
                      helperText={
                        dateFieldErrors.mitreAttackReceivedDate
                          ? dateFieldErrors.mitreAttackReceivedDateMessage ||
                            'Invalid date format'
                          : ''
                      }
                      error={dateFieldErrors.mitreAttackReceivedDate}
                      label="DATE RECEIVED"
                      onDateChange={(date, inputValue) => {
                        if (isMitraAttact) {
                          const { isInvalid, errorMessage } = validateSetErrors(
                            'mitreAttackReceivedDate',
                            date,
                            inputValue,
                            formMethods
                          );
                          setDateFieldErrors((prev) => ({
                            ...prev,
                            mitreAttackReceivedDate: isInvalid,
                            mitreAttackReceivedDateMessage: errorMessage,
                          }));
                          if (!isInvalid && date) {
                            MitreAttactHandler(
                              date ? date.toISOString() : null,
                              'mitreAttackReceivedDate'
                            );
                          }
                        }
                      }}
                      data-testid="mitre-attack-received-date-input"
                    />
                  </Box>
                </Box>
              </Box>
            )}
          </ConvergeDetailAccordion>
        </FormProvider>
      </Paper>
    </Box>
  );
};
export default MitreAttack;
